import { createSignal, Show, type JSX } from 'solid-js';
import { A } from '@solidjs/router';
import { authStore } from '@stores/session';

type Props = {
  // Message shown to signed-in-but-unverified users in place of gated content.
  message: string;
  // Optional custom fallback (e.g. blurred preview) shown alongside the CTA.
  children?: JSX.Element;
};

// Renders gated content ONLY if the current user has `medical_status = 'approved'`.
// - Signed out → sign-in prompt
// - Signed in, not verified → clinic submission form + custom message
// - Verified → children (gated content)
//
// The inverse of the common wrapper pattern: the caller passes the gated
// content via a render prop rather than children, because `children` is
// reserved for the fallback preview.
export default function MedicalStatusGate(props: Props & { when: () => JSX.Element }): JSX.Element {
  return (
    <Show
      when={authStore.user()}
      fallback={
        <GateShell message={props.message}>
          <p class="text-sm">
            <A href="/sign-in" class="underline font-medium">Sign in</A> to access live stock
            information.
          </p>
          {props.children}
        </GateShell>
      }
    >
      <Show
        when={authStore.isMedicalPatient()}
        fallback={
          <GateShell message={props.message}>
            <ClinicSubmissionForm />
            {props.children}
          </GateShell>
        }
      >
        {props.when()}
      </Show>
    </Show>
  );
}

function GateShell(shellProps: { message: string; children: JSX.Element }): JSX.Element {
  return (
    <div class="rounded-xl border border-brand-hex bg-brand-cream/40 p-5 flex flex-col gap-3">
      <p class="text-sm text-brand-green/90">{shellProps.message}</p>
      {shellProps.children}
    </div>
  );
}

export function ClinicSubmissionForm(): JSX.Element {
  const [clinic, setClinic] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const status = () => authStore.medicalStatus();

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authStore.submitClinic(clinic());
      setClinic('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show
      when={status() !== 'pending'}
      fallback={
        <div class="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Your clinic submission is awaiting admin review. You'll get access as soon as it's approved.
        </div>
      }
    >
      <Show
        when={status() !== 'rejected'}
        fallback={
          <div class="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
            Your previous clinic submission was rejected. Please double-check the clinic name and try again.
          </div>
        }
      >
        <form onSubmit={submit} class="flex flex-col gap-2">
          <label class="text-xs font-medium">
            UK Medical Patient Authorisation Code
            <input
              type="text" name="text"
              value={clinic()}
              onInput={(e) => setClinic(e.currentTarget.value)}
              placeholder="Patient ID (no identifiable information)"
              required
              class="mt-1 w-full rounded-md border border-brand-hex px-3 py-2 text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={busy() || !clinic().trim()}
            class="self-start rounded-full bg-brand-moss text-brand-cream text-xs font-semibold px-4 py-2 disabled:opacity-50"
          >
            {busy() ? 'Submitting…' : 'Submit for verification'}
          </button>
          <Show when={error()}>
            <p class="text-xs text-red-700">{error()}</p>
          </Show>
        </form>
      </Show>
    </Show>
  );
}
